import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Eye, Share2, User, ShoppingCart, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import ProtectedImage from '../components/ProtectedImage';
import { Button } from '../components/ui/button';
import { API } from '../config/api';

const categoryLabels = {
  photography: 'Photography',
  fine_art: 'Fine Art',
  film: 'Film',
};

// ---- Sub-components ----

function ArtworkImage({ artwork }) {
  return (
    <div className="relative">
      <ProtectedImage
        src={artwork.display_url || artwork.watermark_url || artwork.image_url}
        alt={artwork.title}
        className="w-full aspect-square object-cover"
      />
      {!artwork.has_purchased && (
        <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/80 px-3 py-2 text-xs text-[#A1A1AA]">
          <Shield size={14} className="text-[#D4AF37]" />
          Protected Preview
        </div>
      )}
    </div>
  );
}

function ArtworkActions({ artwork, liked, favorited, onLike, onFavorite, onShare }) {
  return (
    <div className="mt-8 flex items-center gap-6">
      <span className="flex items-center gap-2 text-[#71717A]">
        <Eye size={18} />
        {artwork.view_count} views
      </span>
      <button
        onClick={onLike}
        className={`flex items-center gap-2 transition-colors ${liked ? 'text-[#EF4444]' : 'text-[#71717A] hover:text-[#EF4444]'}`}
        data-testid="like-btn"
      >
        <Heart size={18} fill={liked ? 'currentColor' : 'none'} />
        {artwork.like_count} likes
      </button>
      <button
        onClick={onShare}
        className="flex items-center gap-2 text-[#71717A] hover:text-[#F5F5F0] transition-colors"
        data-testid="share-btn"
      >
        <Share2 size={18} />
        Share
      </button>
      <button
        onClick={onFavorite}
        className={`flex items-center gap-2 transition-colors ${favorited ? 'text-[#D4AF37]' : 'text-[#71717A] hover:text-[#D4AF37]'}`}
        data-testid="favorite-btn"
      >
        <Heart size={18} fill={favorited ? 'currentColor' : 'none'} />
        {favorited ? 'Saved' : 'Save'}
      </button>
    </div>
  );
}

function PurchaseSection({ artwork, addingToCart, onAddToCart }) {
  return (
    <div className="mt-12 pt-8 border-t border-white/10">
      <div className="flex items-baseline gap-4">
        <span className="font-serif text-4xl text-[#D4AF37]">
          ${artwork.price?.toFixed(2)}
        </span>
        {artwork.has_purchased && (
          <span className="text-[#10B981] text-sm">You own this</span>
        )}
      </div>

      {!artwork.is_sold && !artwork.has_purchased && (
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <Button
            onClick={onAddToCart}
            disabled={addingToCart}
            className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="add-to-cart-btn"
          >
            <ShoppingCart size={18} />
            {addingToCart ? 'Adding...' : 'Add to Cart'}
          </Button>
        </div>
      )}

      {artwork.has_purchased && (
        <div className="mt-6 p-4 bg-[#10B981]/10 border border-[#10B981]/30">
          <p className="text-[#10B981] text-sm">
            You have full access to this artwork. Download available in your purchases.
          </p>
        </div>
      )}
    </div>
  );
}

// ---- Main Component ----

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  const fetchArtwork = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/artworks/${id}`);
      setArtwork(response.data);
      setFavorited(response.data.is_favorited || false);
    } catch (error) {
      console.error('Error fetching artwork:', error);
      toast.error('Artwork not found');
      navigate('/gallery');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchArtwork();
  }, [fetchArtwork]);

  const handleLike = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const response = await axios.post(`${API}/artworks/${id}/like`);
      setLiked(response.data.liked);
      setArtwork(prev => ({
        ...prev,
        like_count: response.data.liked ? prev.like_count + 1 : prev.like_count - 1
      }));
    } catch {
      toast.error('Failed to like artwork');
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const response = await axios.post(`${API}/artworks/${id}/favorite`);
      setFavorited(response.data.favorited);
      toast.success(response.data.favorited ? 'Added to favorites! You\'ll be notified of new drops.' : 'Removed from favorites');
    } catch {
      toast.error('Failed to update favorite');
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      setAddingToCart(true);
      await addToCart(artwork.id);
    } catch {
      // Error already handled by CartContext toast
    } finally {
      setAddingToCart(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            <div className="aspect-square skeleton" />
            <div className="space-y-6">
              <div className="h-8 w-32 skeleton" />
              <div className="h-12 w-3/4 skeleton" />
              <div className="h-24 skeleton" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!artwork) return null;

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} />
          Back to Gallery
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          <ArtworkImage artwork={artwork} />

          <div className="lg:py-8">
            {/* Badges */}
            <div className="flex items-center gap-4 mb-4">
              <span className="category-badge">
                {categoryLabels[artwork.category] || artwork.category}
              </span>
              {artwork.is_featured && <span className="badge badge-gold">Featured</span>}
              {artwork.is_sold && <span className="badge badge-danger">Sold</span>}
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0]">
              {artwork.title}
            </h1>

            <button
              onClick={() => navigate(`/artist/${artwork.artist_id}`)}
              className="mt-4 flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
              data-testid="artist-link"
            >
              <User size={16} />
              {artwork.artist_name}
            </button>

            <p className="mt-8 text-[#A1A1AA] leading-relaxed">{artwork.description}</p>

            {artwork.tags?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {artwork.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-white/5 text-[#71717A] text-sm">#{tag}</span>
                ))}
              </div>
            )}

            <ArtworkActions
              artwork={artwork}
              liked={liked}
              favorited={favorited}
              onLike={handleLike}
              onFavorite={handleFavorite}
              onShare={handleShare}
            />

            <PurchaseSection
              artwork={artwork}
              addingToCart={addingToCart}
              onAddToCart={handleAddToCart}
            />

            {/* Artist Commission Info */}
            <div className="mt-8 p-4 bg-[#121212] border border-white/10">
              <p className="text-xs uppercase tracking-[0.1em] text-[#71717A] mb-2">Artist Receives</p>
              <p className="text-[#F5F5F0]">
                ${(artwork.price * 0.82).toFixed(2)} <span className="text-[#71717A]">(82% of sale)</span>
              </p>
            </div>

            {/* Copyright Notice */}
            <div className="mt-6 p-3 border border-white/5">
              <p className="text-[#71717A] text-xs leading-relaxed">
                &copy; Scott McGuire / mortalworks.art &mdash; All Rights Reserved. Unauthorized use, copying, or screenshots for commercial purposes is prohibited.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
